import * as R from 'ramda';
import * as P from 'plow-js';
import { FieldArray } from 'formik';
import React, { Fragment } from 'react';
// features
import PC from '../../../permission/role-permission';
import { CostsSection } from '../../../work-order/components/costs-section';
// components
import { TextComponent } from '../../../../components/text';
import { FormSectionHeader } from '../../../../components/form-section-header';
// forms
import { Fieldset2 } from '../../../../forms/formik';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
import { ENUMS } from '../../../../constants/enums';
// icons
import * as I from '../../../../svgs';
// ui
import { Box, Flex, MainActionButton } from '../../../../ui';
// feature fleet
import {
  chargeDefaultSettings,
  invoiceDefaultFields2,
} from '../../equipment-service/settings/field-settings';
//////////////////////////////////////////////////

const darkBlueColor = G.getTheme('colors.dark.blue');

const fieldsWrapperStyles = { mb: 30, width: '100%' };

const inputWrapperStyles = { mr: 15, flexGrow: 1, flexBasis: 110 };

const inputWrapperStyles2 = { flexGrow: 1, flexBasis: 110 };

const inputWrapperStyles3 = { mr: 15, flexGrow: 1, flexBasis: 75 };

const inputWrapperStyles4 = { flexGrow: 1, flexBasis: 70 };

const fieldsetSettings = [
  {
    fieldsWrapperStyles,
    fields: [
      {
        type: 'text',
        isRequired: true,
        inputWrapperStyles,
        fieldName: GC.FIELD_INVOICE_NUMBER,
        label: ['titles:invoice-number', 'Invoice Number'],
      },
      {
        type: 'select',
        inputWrapperStyles,
        options: 'invoiceStatusOptions',
        fieldName: GC.FIELD_INVOICE_STATUS,
        label: ['titles:invoice-status', 'Invoice Status'],
      },
      {
        inputWrapperStyles,
        type: 'reactSelect',
        fieldName: GC.FIELD_SERVICE_VENDOR_GUID,
        options: 'serviceVendorListAvailableOptions',
        label: ['titles:service-vendor', 'Service Vendor'],
      },
      {
        isRequired: true,
        isClearable: true,
        type: 'datePicker',
        shouldCustomChange: true,
        inputWrapperStyles: inputWrapperStyles2,
        label: ['titles:invoice-date', 'Invoice Date'],
        fieldName: GC.FIELD_INVOICE_DATE,
        customChangeHandler2: ({ value, props }) => {
          const { name, values, setValues } = props;

          const invoiceDate = G.convertInstanceToDefaultDateFormat(value);

          const performedDate = R.propOr(invoiceDate, GC.FIELD_PERFORMED_DATE, values);

          const newValues = P.$all(
            P.$set(name, invoiceDate),
            P.$set(GC.FIELD_STATUS, ENUMS.ENUM_COMPLETED),
            P.$set(GC.FIELD_PERFORMED_DATE, performedDate),
            values,
          );

          return setValues(newValues);
        },
      },
    ],
  },
  {
    fieldsWrapperStyles,
    fields: [
      {
        type: 'select',
        isRequired: true,
        options: GC.CURRENCY_OPTIONS,
        fieldName: GC.FIELD_CURRENCY,
        label: ['titles:currency', 'Currency'],
        inputWrapperStyles: inputWrapperStyles3,
      },
      {
        type: 'select',
        options: 'glCodeOptions',
        fieldName: GC.FIELD_GL_CODE,
        label: ['titles:gl-code', 'GL Code'],
        inputWrapperStyles: inputWrapperStyles3,
        disabled: () => G.hasNotAmousCurrentUserPermissions(PC.GL_CODE_WRITE),
      },
      {
        type: 'text',
        fieldName: GC.FIELD_PO_NUMBER,
        inputWrapperStyles: inputWrapperStyles3,
        label: ['titles:po-number', 'PO Number'],
      },
      {
        type: 'text',
        fieldName: GC.FIELD_INVOICE_NET_DAYS,
        label: ['titles:net-days', 'Ned Days'],
        inputWrapperStyles: inputWrapperStyles3,
      },
      {
        isClearable: true,
        type: 'datePicker',
        inputWrapperStyles: inputWrapperStyles2,
        fieldName: GC.FIELD_INVOICE_PAYMENT_DUE_DATE,
        label: ['titles:payment-due-date', 'Payment Due Date'],
      },
    ],
  },
  {
    fieldsWrapperStyles,
    fields: [
      {
        type: 'text',
        inputWrapperStyles: inputWrapperStyles3,
        fieldName: GC.FIELD_INVOICE_CHECK_NUMBER,
        label: ['titles:check-number', 'Check Number'],
      },
      {
        isClearable: true,
        type: 'datePicker',
        inputWrapperStyles,
        shouldCustomChange: true,
        fieldName: GC.FIELD_INVOICE_CHECK_DATE,
        label: ['titles:check-date', 'Check Date'],
      },
      {
        type: 'text',
        fieldName: GC.FIELD_CHECK_AMOUNT,
        inputWrapperStyles: inputWrapperStyles3,
        label: ['titles:check-amount', 'Check Amount'],
      },
      {
        isClearable: true,
        type: 'datePicker',
        inputWrapperStyles,
        fieldName: GC.FIELD_INVOICE_DEPOSIT_DATE,
        label: ['titles:deposit-date', 'Deposit Date'],
      },
      {
        type: 'text',
        label: ['titles:ach', 'ACH'],
        inputWrapperStyles: inputWrapperStyles4,
        fieldName: GC.FIELD_INVOICE_ACH_PAYMENT_CONFIRMATION_NUMBER,
      },
    ],
  },
  {
    fieldsWrapperStyles: { width: '100%' },
    fields: [
      {
        type: 'textarea',
        inputStyles: {
          p: 15,
          height: 100,
        },
        inputWrapperStyles: {
          flexGrow: 1,
          flexBasis: 300,
        },
        fieldName: GC.FIELD_COMMENTS,
        label: ['titles:comments', 'Comments'],
      },
    ],
  },
];

const chargeFieldSettings = [
  {
    type: 'text',
    isRequired: true,
    label: ['titles:name', 'Name'],
    fieldName: GC.FIELD_DISPLAYED_NAME,
    inputWrapperStyles: { mr: 15, width: 200 },
  },
  {
    type: 'text',
    isRequired: true,
    fieldName: GC.FIELD_TOTAL,
    label: ['titles:total', 'Total'],
    inputWrapperStyles: { mr: 15, width: 100 },
  },
  {
    type: 'text',
    fieldName: GC.FIELD_COMMENTS,
    inputWrapperStyles: { width: 240 },
    label: ['titles:comments', 'Comments'],
  },
];

const ChargesSection = (props) => {
  const { push, remove, values, itemIndex, chargesTotal } = props;

  const charges = R.pathOr([], [GC.FIELD_INVOICES, itemIndex, GC.FIELD_CHARGES], values);

  const chargesLength = R.length(charges);

  return (
    <FormSectionHeader
      expanded={true}
      action={() => push(chargeDefaultSettings())}
      title={G.getWindowLocale('titles:charges', 'Charges')}
    >
      {
        charges.map((_, index) => (
          <Flex my={25} key={index}>
            <Box
              mx={10}
              display='flex'
              cursor='pointer'
              alignItems='center'
              onClick={() => remove(index)}
              title={G.getWindowLocale('titles:remove', 'Remove')}
            >
              {I.trash(darkBlueColor)}
            </Box>
            <Fieldset2
              {...G.getFormikProps(props)}
              {...G.getArrayFormikProps(props)}
              itemIndex={index}
              arrayLength={chargesLength}
              fields={chargeFieldSettings}
              arrayName={`${GC.FIELD_INVOICES}.${itemIndex}.${GC.FIELD_CHARGES}`}
            />
          </Flex>
        ))
      }
      {
        G.isNotNilAndNotEmpty(charges) &&
        <Flex px={15} width='100%' flexShrink={0} justifyContent='flex-end'>
          <Box
            mr='8px'
            fontWeight={700}
            textTransform='uppercase'
            color={G.getTheme('colors.darkGrey')}
          >
            {G.getWindowLocale('titles:subtotal', 'Subtotal')}:
          </Box>
          <TextComponent
            maxWidth={300}
            display='block'
            fontWeight={700}
            title={chargesTotal}
            withEllipsis={true}
          >
            {chargesTotal}
          </TextComponent>
        </Flex>
      }
    </FormSectionHeader>
  );
};

const getChargesTotal = (invoice) => R.compose(
  G.NaNToZero,
  G.toFixed,
  R.sum,
  R.map(R.pathOr(0, [GC.FIELD_TOTAL])),
  R.pathOr([], [GC.FIELD_CHARGES]),
)(invoice);

const InvoiceForm = (props) => {
  const {
    values,
    glCodeOptions,
    setFieldValue,
    leftActiveTab,
    branchCurrency,
    setLeftActiveTab,
    invoiceStatusOptions,
    serviceVendorListAvailableOptions,
  } = props;

  const { invoices } = values;

  const itemIndex = R.subtract(leftActiveTab, 3);

  const invoice = R.path([itemIndex], invoices);

  const chargesTotal = getChargesTotal(invoice);

  return (
    <Fragment>
      <Box p={15}>
        <Fieldset2
          {...G.getFormikProps(props)}
          grouped={true}
          fieldsetType='array'
          itemIndex={itemIndex}
          fields={fieldsetSettings}
          arrayName={GC.FIELD_INVOICES}
          glCodeOptions={glCodeOptions}
          invoiceStatusOptions={invoiceStatusOptions}
          serviceVendorListAvailableOptions={serviceVendorListAvailableOptions}
        />
      </Box>
      <FieldArray
        name={`${GC.FIELD_INVOICES}.${itemIndex}.${GC.FIELD_CHARGES}`}
        render={(arrayHelpers) => (
          <ChargesSection {...props} {...arrayHelpers} itemIndex={itemIndex} chargesTotal={chargesTotal} />
        )}
      />
      <CostsSection
        {...props}
        version={2}
        additionTotal={chargesTotal}
        objectName={`${GC.FIELD_INVOICES}.${itemIndex}`}
      />
      <Flex my={15} px={15} justifyContent='space-between'>
        <MainActionButton
          type='button'
          onClick={() => setFieldValue(
            GC.FIELD_INVOICES, [...invoices, R.assoc(GC.FIELD_CURRENCY, branchCurrency, invoiceDefaultFields2)],
          )}
        >
          {G.getAddTitle(['titles:new-invoice', 'New Invoice'])}
        </MainActionButton>
        {
          R.gt(R.length(invoices), 1) &&
          <MainActionButton
            type='button'
            onClick={() => {
              setLeftActiveTab(0);
              setFieldValue(GC.FIELD_INVOICES, R.remove(itemIndex, 1, invoices));
            }}
          >
            {G.getWindowLocale('titles:remove-invoice', 'Remove Invoice')}
          </MainActionButton>
        }
      </Flex>
    </Fragment>
  );
};

export default InvoiceForm;
